<script lang="ts" setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import type { FulfillmentTasksResponse, FulfillmentTask } from '@/types/fulfillment'

defineOptions({ name: 'FulfillmentTasksList' })

const loading = ref(true)
const tasks = ref<FulfillmentTask[]>([])
const pagination = usePagination({ initialItemsPerPage: 50 })

// Filters
const statusFilter = ref<string>('all')
const cartIdFilter = ref('')

const statusOptions = [
  { value: 'all', title: 'Alle Status' },
  { value: 'pending', title: 'Ausstehend' },
  { value: 'in_progress', title: 'In Bearbeitung' },
  { value: 'completed', title: 'Abgeschlossen' },
  { value: 'failed', title: 'Fehlgeschlagen' },
  { value: 'compensated', title: 'Kompensiert' }
]

// Fetch tasks with filters
async function fetchTasks() {
  loading.value = true

  try {
    const { limit, offset } = pagination.buildQueryParams()
    let url = `/admin/fulfillment/tasks?limit=${limit}&offset=${offset}`

    if (statusFilter.value !== 'all') {
      url += `&status=${statusFilter.value}`
    }
    if (cartIdFilter.value.trim()) {
      url += `&cart_id=${cartIdFilter.value.trim()}`
    }

    const res = await useAxios<null, FulfillmentTasksResponse>(
      'GET',
      url,
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      tasks.value = res.data.tasks ?? []
      pagination.setTotal(res.data.count ?? 0)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'FulfillmentTasksList' })
  } finally {
    loading.value = false
  }
}

// Watch for filter and pagination changes
watch(() => pagination.page.value, () => {
  fetchTasks()
})

watch([statusFilter], () => {
  pagination.goToPage(1)
  fetchTasks()
})

function applyCartIdFilter() {
  pagination.goToPage(1)
  fetchTasks()
}

function clearFilters() {
  statusFilter.value = 'all'
  cartIdFilter.value = ''
  pagination.goToPage(1)
  fetchTasks()
}

// Pagination display helpers
const showingFrom = computed(() => {
  if (!tasks.value?.length) return 0
  return ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1
})

const showingTo = computed(() => {
  return Math.min(showingFrom.value + (tasks.value?.length ?? 0) - 1, pagination.total.value)
})

function formatDate(dateString: string): string {
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'completed': return 'status-completed'
    case 'failed': return 'status-failed'
    case 'in_progress': return 'status-progress'
    case 'pending': return 'status-pending'
    case 'compensated': return 'status-compensated'
    default: return ''
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'completed': return 'Abgeschlossen'
    case 'failed': return 'Fehlgeschlagen'
    case 'in_progress': return 'In Bearbeitung'
    case 'pending': return 'Ausstehend'
    case 'compensated': return 'Kompensiert'
    default: return status
  }
}

function getItemTypeLabel(itemType: string): string {
  switch (itemType) {
    case 'domain_registration': return 'Domain-Registrierung'
    case 'domain_transfer': return 'Domain-Transfer'
    case 'internal_transfer': return 'Interner Transfer'
    case 'hosting': return 'Hosting'
    case 'k8s': return 'Kubernetes'
    default: return itemType
  }
}

function truncateId(id: string): string {
  if (id.length <= 12) return id
  return `${id.substring(0, 6)}...${id.substring(id.length - 4)}`
}

onMounted(() => {
  fetchTasks()
})
</script>

<template>
  <div class="tasks-list">
    <!-- Header -->
    <div class="list-header">
      <div class="header-left">
        <div class="header-icon">
          <v-icon icon="mdi-format-list-checks" size="20" />
        </div>
        <div>
          <h2 class="list-title">Fulfillment Tasks</h2>
          <p class="list-subtitle">
            {{ pagination.total.value }} Tasks insgesamt
          </p>
        </div>
      </div>
      <button class="refresh-btn" @click="fetchTasks">
        <v-icon icon="mdi-refresh" size="18" />
      </button>
    </div>

    <!-- Filters -->
    <div class="filters-container">
      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        item-title="title"
        item-value="value"
        label="Status"
        variant="outlined"
        density="compact"
        hide-details
        class="filter-select glass-input"
      />
      <v-text-field
        v-model="cartIdFilter"
        label="Cart ID"
        placeholder="UUID eingeben..."
        variant="outlined"
        density="compact"
        hide-details
        clearable
        class="filter-input glass-input"
        @keyup.enter="applyCartIdFilter"
      >
        <template #append-inner>
          <v-icon icon="mdi-magnify" size="18" class="search-icon" @click="applyCartIdFilter" />
        </template>
      </v-text-field>
      <button
        v-if="statusFilter !== 'all' || cartIdFilter"
        class="clear-filters-btn"
        @click="clearFilters"
      >
        <v-icon icon="mdi-close" size="16" />
        Filter zurücksetzen
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular color="orange" indeterminate size="40" width="3" />
      <span class="loading-text">Lade Tasks...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="tasks.length === 0" class="empty-state">
      <div class="empty-icon">
        <v-icon icon="mdi-clipboard-check-outline" size="48" />
      </div>
      <span class="empty-title">Keine Tasks gefunden</span>
      <span class="empty-subtitle">
        {{ statusFilter !== 'all' || cartIdFilter ? 'Versuche andere Filter' : 'Noch keine Fulfillment-Tasks vorhanden' }}
      </span>
    </div>

    <!-- Tasks Table -->
    <template v-else>
      <div class="tasks-table">
        <div class="table-head">
          <div class="head-cell cell-id">Task ID</div>
          <div class="head-cell cell-cart">Cart ID</div>
          <div class="head-cell cell-account">Kunde</div>
          <div class="head-cell cell-type">Typ</div>
          <div class="head-cell cell-status">Status</div>
          <div class="head-cell cell-step">Step</div>
          <div class="head-cell cell-attempts">Versuche</div>
          <div class="head-cell cell-date">Erstellt</div>
        </div>

        <div
          v-for="task in tasks"
          :key="task.id"
          class="table-row"
          :class="{ 'row-failed': task.status === 'failed' }"
        >
          <div class="row-cell cell-id">
            <span class="task-id">{{ truncateId(task.id) }}</span>
          </div>
          <div class="row-cell cell-cart">
            <span class="cart-id">{{ truncateId(task.cart_id) }}</span>
          </div>
          <div class="row-cell cell-account">
            <span class="account-email">{{ task.account_email }}</span>
          </div>
          <div class="row-cell cell-type">
            <span class="type-badge">{{ getItemTypeLabel(task.item_type) }}</span>
          </div>
          <div class="row-cell cell-status">
            <span class="status-badge" :class="getStatusClass(task.status)">
              {{ getStatusLabel(task.status) }}
            </span>
          </div>
          <div class="row-cell cell-step">
            <span class="step-num">{{ task.step }}</span>
          </div>
          <div class="row-cell cell-attempts">
            <span class="attempts-num">{{ task.attempts }}</span>
          </div>
          <div class="row-cell cell-date">
            <span class="date-text">{{ formatDate(task.created_at) }}</span>
          </div>
        </div>
      </div>

      <!-- Error Row (if task has error) -->
      <div
        v-for="task in tasks.filter(t => t.error)"
        :key="`error-${task.id}`"
        class="error-row"
      >
        <v-icon icon="mdi-alert-circle" size="14" />
        <span class="error-task-id">{{ truncateId(task.id) }}:</span>
        <span class="error-message">{{ task.error }}</span>
      </div>

      <!-- Pagination -->
      <div class="pagination-container">
        <span class="pagination-info">
          Zeige {{ showingFrom }}-{{ showingTo }} von {{ pagination.total.value }}
        </span>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="!pagination.hasPrevPage.value"
            @click="pagination.prevPage()"
          >
            <v-icon icon="mdi-chevron-left" size="18" />
          </button>
          <span class="page-indicator">
            Seite {{ pagination.page.value }} von {{ pagination.totalPages.value }}
          </span>
          <button
            class="page-btn"
            :disabled="!pagination.hasNextPage.value"
            @click="pagination.nextPage()"
          >
            <v-icon icon="mdi-chevron-right" size="18" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.tasks-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 10px;

  .v-icon {
    color: #00b0ff;
  }
}

.list-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.list-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.refresh-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
    color: rgba(255, 255, 255, 0.9);
  }
}

/* Filters */
.filters-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 14px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;
}

.filter-select {
  min-width: 180px;
}

.filter-input {
  min-width: 240px;

  .search-icon {
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }
}

.clear-filters-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(239, 83, 80, 0.1);
  border: 1px solid rgba(239, 83, 80, 0.2);
  border-radius: 6px;
  color: #ef5350;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 83, 80, 0.15);
  }
}

/* Loading & Empty States */
.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 200px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.loading-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;

  .v-icon {
    color: rgba(200, 218, 255, 0.5);
  }
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.empty-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Table */
.tasks-table {
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.table-head {
  display: grid;
  grid-template-columns: 100px 100px 1fr 140px 110px 60px 70px 130px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.head-cell {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.table-row {
  display: grid;
  grid-template-columns: 100px 100px 1fr 140px 110px 60px 70px 130px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &.row-failed {
    background: rgba(239, 83, 80, 0.03);
    border-left: 3px solid #ef5350;
  }
}

.row-cell {
  display: flex;
  align-items: center;
}

.task-id,
.cart-id {
  font-family: monospace;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.6);
}

.account-email {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-badge {
  padding: 3px 8px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 4px;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.8);
}

.step-num,
.attempts-num {
  font-weight: 600;
  color: rgba(200, 218, 255, 0.7);
}

.date-text {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
}

/* Status Badges */
.status-badge {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;

  &.status-completed {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
  }

  &.status-failed {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }

  &.status-progress {
    background: rgba(0, 176, 255, 0.15);
    color: #00b0ff;
  }

  &.status-pending {
    background: rgba(255, 193, 7, 0.15);
    color: #ffc107;
  }

  &.status-compensated {
    background: rgba(156, 39, 176, 0.15);
    color: #9c27b0;
  }
}

/* Error Row */
.error-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(239, 83, 80, 0.05);
  border: 1px solid rgba(239, 83, 80, 0.15);
  border-radius: 8px;
  margin-top: 8px;

  .v-icon {
    color: #ef5350;
  }
}

.error-task-id {
  font-family: monospace;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
}

.error-message {
  font-size: 0.8rem;
  color: #ef5350;
}

/* Pagination */
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba(15, 23, 48, 0.3);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
    color: rgba(255, 255, 255, 0.9);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

/* Light Theme */
.v-theme--lightTheme {
  .list-title,
  .empty-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .list-subtitle,
  .empty-subtitle,
  .loading-text {
    color: rgba(15, 23, 42, 0.6);
  }

  .tasks-table,
  .loading-container,
  .empty-state,
  .filters-container {
    background: rgba(255, 255, 255, 0.6);
  }

  .account-email {
    color: rgba(15, 23, 42, 0.9);
  }

  .task-id,
  .cart-id,
  .date-text {
    color: rgba(15, 23, 42, 0.6);
  }
}

/* Responsive */
@media (max-width: 1100px) {
  .table-head,
  .table-row {
    grid-template-columns: 90px 90px 1fr 100px 80px 50px;
  }

  .cell-attempts,
  .cell-date {
    display: none;
  }
}

@media (max-width: 768px) {
  .filters-container {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    width: 100%;
  }

  .filter-select,
  .filter-input {
    width: 100%;
  }

  .table-head {
    display: none;
  }

  .table-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px;
  }

  .row-cell {
    width: 100%;
    justify-content: space-between;
  }

  .pagination-container {
    flex-direction: column;
    gap: 12px;
  }
}
</style>

<!-- Unscoped styles for Vuetify components - SSR compatibility -->
<style lang="scss">
/* Glass Input Styling for Vuetify components */
.glass-input .v-field {
  background: rgba(255, 255, 255, 0.04) !important;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.glass-input .v-field:hover {
  background: rgba(255, 255, 255, 0.06) !important;
}

.glass-input .v-field--focused {
  background: rgba(255, 255, 255, 0.08) !important;
}

.glass-input .v-field__overlay {
  background: transparent !important;
}

.glass-input .v-field__outline {
  --v-field-border-opacity: 0.15;
  color: rgba(120, 163, 255, 1);
}

.glass-input .v-field__outline__start,
.glass-input .v-field__outline__end {
  border-color: rgba(120, 163, 255, var(--v-field-border-opacity));
}

.glass-input .v-field__input,
.glass-input .v-select__selection-text {
  color: rgba(255, 255, 255, 0.95);
}

.glass-input .v-label {
  color: rgba(200, 218, 255, 0.6);
}

.glass-input .v-field__clearable {
  color: rgba(200, 218, 255, 0.5);
}

/* Light Theme */
.v-theme--lightTheme .glass-input .v-field {
  background: rgba(255, 255, 255, 0.8) !important;
}

.v-theme--lightTheme .glass-input .v-field__outline {
  color: rgba(100, 116, 139, 1);
}

.v-theme--lightTheme .glass-input .v-field__outline__start,
.v-theme--lightTheme .glass-input .v-field__outline__end {
  border-color: rgba(100, 116, 139, var(--v-field-border-opacity));
}

.v-theme--lightTheme .glass-input .v-field__input,
.v-theme--lightTheme .glass-input .v-select__selection-text {
  color: rgba(15, 23, 42, 0.9);
}

.v-theme--lightTheme .glass-input .v-label {
  color: rgba(15, 23, 42, 0.6);
}
</style>
